import React, { useState } from "react";
import { motion } from "framer-motion";
import { Edit, Trash2, Eye, ThumbsUp, ChevronDown, ChevronUp } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/badge";
import { CommonQuestion } from "../../modules/shared/model/common-questions.interfaces";

interface FaqCardProps {
    faq: CommonQuestion;
    onEdit: (faq: CommonQuestion) => void;
    onDelete: (faq: CommonQuestion) => void;
    onView?: (faq: CommonQuestion) => void;
}

const FaqCard: React.FC<FaqCardProps> = React.memo(({ faq, onEdit, onDelete, onView }) => {
    const [showAllCountries, setShowAllCountries] = useState(false);

    const handleEdit = () => {
        onEdit(faq);
    };

    const handleDelete = () => {
        onDelete(faq);
    };

    const handleView = () => {
        onView?.(faq);
    };

    const toggleCountries = () => {
        setShowAllCountries(!showAllCountries);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
        >
            <Card className="hover:shadow-md transition-shadow" style={{ textAlign: "left" }}>
                <div className="p-6 space-y-4">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap gap-2 mb-2 items-center">
                                {faq.category && (
                                    <Badge variant="outline">
                                        {faq.category.name}
                                    </Badge>
                                )}
                                {faq.countries && faq.countries.length > 0 && (
                                    <>
                                        {(showAllCountries ? faq.countries : faq.countries.slice(0, 2)).map((country, index) => (
                                            <Badge key={`country-${country.id}-${index}`} variant="secondary" className="bg-blue-100 text-blue-800">
                                                {country.code ? `${country.code}` : country.name || `Country ${country.id}`}
                                            </Badge>
                                        ))}
                                        {faq.countries.length > 2 && (
                                            <button
                                                onClick={toggleCountries}
                                                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                            >
                                                {showAllCountries ? (
                                                    <>
                                                        Show less
                                                        <ChevronUp className="h-3 w-3" />
                                                    </>
                                                ) : (
                                                    <>
                                                        +{faq.countries.length - 2} more
                                                        <ChevronDown className="h-3 w-3" />
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                            <h3 className="font-medium text-gray-900 line-clamp-2">
                                {faq.question || "No question provided"}
                            </h3>
                        </div>
                        <div className="flex gap-2 ml-4 flex-shrink-0">
                            {onView && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleView}
                                    className="text-blue-600 hover:text-blue-700"
                                >
                                    <Eye className="h-4 w-4" />
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleEdit}
                                className="text-gray-600 hover:text-gray-700"
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDelete}
                                className="text-red-600 hover:text-red-700"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Answer */}
                    <div className="text-gray-600">
                        {faq.answer ? (
                            <p className="whitespace-pre-line line-clamp-3">
                                {faq.answer}
                            </p>
                        ) : (
                            <p className="text-gray-400 italic">No answer provided</p>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            <span>{faq.views || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <ThumbsUp className="h-4 w-4" />
                            <span>{faq.likes || 0}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                            Updated {new Date(faq.updatedAt).toLocaleDateString()}
                        </div>
                    </div>

                    {/* Languages */}
                    {faq.languages && faq.languages.length > 0 && (
                        <div className="pt-4 border-t">
                            <div className="flex flex-wrap gap-2">
                                {faq.languages.map((lang, index) => (
                                    <Badge key={`lang-${index}`} variant="secondary" className="text-xs">
                                        {lang.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </motion.div>
    );
});

FaqCard.displayName = "FaqCard";

export default FaqCard;
